import { type StateCreator } from 'zustand';

/**
 * Manages the push-mode sidebar (screens < XL with canvas open).
 *
 * The push sidebar can be **locked** (e.g. during rename or dropdown
 * interaction) to prevent it from closing, and **suppressed** (e.g. after
 * unpinning) to prevent hover-to-open. Both use token-based tracking so
 * multiple independent consumers can hold locks/suppressions without
 * interfering with each other.
 *
 * A close request while locks are held is deferred and replayed automatically
 * when the last lock is released.
 */
export type WorkspaceSidebarPushSlice = {
  isOpen: boolean;
  lockCount: number;
  isSuppressHoverOpen: boolean;

  openPush: () => void;
  closePush: () => void;
  lockPush: (token: string) => void;
  unlockPush: (token: string) => void;
  deleteLockToken: (token: string) => void;
  suppressHoverOpen: (token: string) => void;
  unsuppressHoverOpen: (token: string) => void;
  reset: () => void;
};

type CreateWorkspaceSidebarPushSlice = () => StateCreator<WorkspaceSidebarPushSlice, [], [], WorkspaceSidebarPushSlice>;

export const createWorkspaceSidebarPushSlice: CreateWorkspaceSidebarPushSlice = () => (set) => {
  const lockTokens = new Set<string>();
  const suppressTokens = new Set<string>();
  let pendingClose = false;

  const closePush = () => {
    if (lockTokens.size > 0) {
      pendingClose = true;
      return;
    }
    pendingClose = false;
    set({ isOpen: false });
  };

  return {
    isOpen: false,
    lockCount: 0,
    isSuppressHoverOpen: false,

    openPush: () => {
      if (suppressTokens.size > 0) {
        return;
      }
      pendingClose = false;
      set({ isOpen: true });
    },

    closePush,

    lockPush: (token) => {
      lockTokens.add(token);
      set({ lockCount: lockTokens.size });
    },

    unlockPush: (token) => {
      lockTokens.delete(token);
      set({ lockCount: lockTokens.size });
      if (lockTokens.size === 0 && pendingClose) {
        closePush();
      }
    },

    deleteLockToken: (token) => {
      lockTokens.delete(token);
      set({ lockCount: lockTokens.size });
    },

    suppressHoverOpen: (token) => {
      suppressTokens.add(token);
      set({ isSuppressHoverOpen: true });
    },

    unsuppressHoverOpen: (token) => {
      suppressTokens.delete(token);
      if (suppressTokens.size === 0) {
        set({ isSuppressHoverOpen: false });
      }
    },

    reset: () => {
      lockTokens.clear();
      suppressTokens.clear();
      pendingClose = false;
      set({ isOpen: false, lockCount: 0, isSuppressHoverOpen: false });
    },
  };
};
